import {
  Delete,
  Edit,
  InsertDriveFile,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Science,
  Summarize,
  Visibility,
} from '@mui/icons-material';
import {
  Chip,
  IconButton,
  LinearProgress,
  SxProps,
  TableCell,
  TableRow,
  TableRowProps,
  Theme,
  Tooltip,
} from '@mui/material';
import { Box, useMediaQuery, useTheme } from '@mui/system';
import { Form, FormStatus, FormTemplateType } from 'api/models';
import useHasAccess from 'hooks/useHasAccess';
import moment from 'moment';
import { query, remove } from 'operations';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { ToastSeverity, confirmDialogState, toastStateSelector } from 'state';
import { Permission } from 'state/permissionState';
import { DialogTypes, GeneralIds, ModelTypes, RoutePaths } from 'types/types';
import { getDateTimeString, getRoutePath } from 'utils';

function getColorByFormStatus(status: FormStatus) {
  switch (status) {
    case FormStatus.Finalized:
      return 'success';
    case FormStatus.New:
      return 'primary';
    default:
      return 'warning';
  }
}

interface ExpandableTableRowProps extends TableRowProps {
  visitId?: string;
  formTemplateTypes?: FormTemplateType[];
  sampleTemplateTypes?: FormTemplateType[];
  reportTemplateTypes?: FormTemplateType[];
  sx?: SxProps<Theme>;
  isOpenByDefault?: boolean;
}

function getFormName(formTemplateTypes: FormTemplateType[], id: string) {
  const formTemplateType = formTemplateTypes.find(
    formTemplateType => formTemplateType.id === id,
  );
  return formTemplateType?.name;
}

export default function ExpandableTableRow({
  visitId,
  children,
  sx,
  isOpenByDefault,
  formTemplateTypes = [],
  sampleTemplateTypes = [],
  reportTemplateTypes = [],
}: ExpandableTableRowProps) {
  const canAddForm = useHasAccess([Permission.WRITE_FORM]);
  const openDialog = useSetRecoilState(confirmDialogState);
  const [isExpanded, setIsExpanded] = useState(isOpenByDefault);
  const setToast = useSetRecoilState(toastStateSelector);
  const formTemplateTypeMap = formTemplateTypes.reduce((m, f) => {
    m.set(f.id, f);
    return m;
  }, new Map());
  const sampleTemplateTypeMap = sampleTemplateTypes.reduce((m, f) => {
    m.set(f.id, f);
    return m;
  }, new Map());

  const reportTemplateTypeMap = reportTemplateTypes.reduce((m, f) => {
    m.set(f.id, f);
    return m;
  }, new Map());

  const [forms, setForms] = useState<Form[]>([]);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { projectId, patientId } = useParams<GeneralIds>();
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setIsLoading(true);
    (async () => {
      const { data: forms } = await query<Form[]>({
        type: ModelTypes.form,
        ids: {
          patientId,
          visitId,
        },
      });

      setForms(
        forms.sort((a, b) => {
          const formDiff =
            (formTemplateTypeMap.get(a.formTemplateTypeId) ? 0 : 1) -
            (formTemplateTypeMap.get(b.formTemplateTypeId) ? 0 : 1);

          return (
            formDiff ||
            moment(b.updatedAt).unix() - moment(a.updatedAt).unix() ||
            0
          );
        }),
      );

      setIsLoading(false);
    })();
    // eslint-disable-next-line
  }, [visitId, patientId, setForms, setIsLoading]);

  const onConfirmDelete = (id: string) => {
    return async () => {
      try {
        await remove({
          type: ModelTypes.form,
          ids: {
            id,
            visitId,
          },
        });
        const { data: forms } = await query<Form[]>({
          type: ModelTypes.form,
          ids: {
            patientId,
            visitId,
          },
        });
        setForms(
          forms.sort((a, b) => {
            const formDiff =
              (formTemplateTypeMap.get(a.formTemplateTypeId) ? 0 : 1) -
              (formTemplateTypeMap.get(b.formTemplateTypeId) ? 0 : 1);

            return (
              formDiff ||
              moment(b.updatedAt).unix() - moment(a.updatedAt).unix() ||
              0
            );
          }),
        );
        openDialog({ isOpen: false });

        setToast({
          isOpen: true,
          message: t('general.success.delete'),
          severity: ToastSeverity.success,
        });
      } catch (e: any) {
        const errorCode = e.response?.data?.error?.[0].code;
        setToast({
          isOpen: true,
          message: i18n.exists(`errors.${errorCode}`)
            ? t(`errors.${errorCode}`)
            : t('projectCreation.errors.deletionFailed'),
          severity: ToastSeverity.error,
        });
      }
    };
  };

  return (
    <>
      <TableRow sx={sx}>
        <TableCell padding="checkbox" sx={{ pl: 2 }}>
          {forms.length > 0 && (
            <IconButton onClick={() => setIsExpanded(!isExpanded)} size="small">
              {isExpanded ? (
                <KeyboardArrowUp fontSize="small" />
              ) : (
                <KeyboardArrowDown fontSize="small" />
              )}
            </IconButton>
          )}
        </TableCell>
        {children}
      </TableRow>
      {isExpanded &&
        (isLoading ? (
          <Box component={'tr'} width="100%" position={'relative'}>
            <Box position={'absolute'} width="100%">
              <LinearProgress />
            </Box>
          </Box>
        ) : (
          forms.map(form => (
            <TableRow
              key={`filled-form-of-${form.id}`}
              sx={{
                bgcolor: theme => theme.palette.action.hover,
              }}
            >
              <TableCell></TableCell>
              <TableCell>
                <Box
                  pl={isMobile ? -4 : 4}
                  display={'flex'}
                  alignItems={'center'}
                >
                  {formTemplateTypeMap.get(form.formTemplateTypeId) && (
                    <InsertDriveFile fontSize="small" sx={{ mr: 1 }} />
                  )}
                  {sampleTemplateTypeMap.get(form.formTemplateTypeId) && (
                    <Science fontSize="small" sx={{ mr: 1 }} />
                  )}
                  {reportTemplateTypeMap.get(form.formTemplateTypeId) && (
                    <Summarize fontSize="small" sx={{ mr: 1 }} />
                  )}
                  {getFormName(
                    [
                      ...formTemplateTypes,
                      ...sampleTemplateTypes,
                      ...reportTemplateTypes,
                    ],
                    form.formTemplateTypeId ?? '',
                  )}
                  <Chip
                    label={t(`forms.status.${form.status}`)}
                    color={getColorByFormStatus(form.status)}
                    sx={{ ml: 1 }}
                    size="small"
                  />
                </Box>
              </TableCell>
              {!isMobile && (
                <TableCell>{getDateTimeString(form.updatedAt)}</TableCell>
              )}
              {!isMobile && (
                <TableCell>{`${form.slimUser?.lastName} ${form.slimUser?.firstName}`}</TableCell>
              )}
              <TableCell align="right">
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    const path = getRoutePath(RoutePaths.formView, {
                      projectId,
                      patientId,
                      visitId,
                      formTemplateTypeId: form.formTemplateTypeId,
                      formId: form.id,
                    });
                    navigate(path);
                  }}
                >
                  <Tooltip title={t('general.view')}>
                    <Visibility fontSize="small" />
                  </Tooltip>
                </IconButton>
                <IconButton
                  size="small"
                  disabled={!canAddForm}
                  onClick={e => {
                    e.stopPropagation();
                    const path = getRoutePath(RoutePaths.formEdit, {
                      projectId,
                      patientId,
                      visitId,
                      formTemplateTypeId: form.formTemplateTypeId,
                      formId: form.id,
                    });
                    navigate(path);
                  }}
                >
                  <Tooltip title={t('general.edit')}>
                    <Edit fontSize="small" />
                  </Tooltip>
                </IconButton>
                <IconButton
                  size="small"
                  disabled={!canAddForm}
                  onClick={e => {
                    e.stopPropagation();
                    openDialog({
                      isOpen: true,
                      dialogType: DialogTypes.confirmDelete,
                      action: onConfirmDelete(form.id),
                    });
                  }}
                >
                  <Tooltip title={t('general.delete')}>
                    <Delete fontSize="small" />
                  </Tooltip>
                </IconButton>
              </TableCell>
            </TableRow>
          ))
        ))}
    </>
  );
}
